import { useCallback, useState } from 'react'
import { Screen } from '@/components/layout'
import { Button, InlineError } from '@/components/primitives'
import { InstitutionRow, List } from '@/components/rows'
import { ErrorState } from '@/components/states'
import { ApiFailure, api, asApiFailure } from '@/lib/api'
import { t, tServer } from '@/lib/copy'
import { LinkUnavailableError, openLink } from '@/lib/plaid'
import { Link, useNavigate, useRoute } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/**
 * Starts a bank connection. The institution picker is Plaid's own hosted UI —
 * this screen only issues the link token, opens Link, and posts the public
 * token back. `?relink={id}` puts Link in update mode for a connection whose
 * consent has lapsed, which is how connectedAccounts recovers reauthRequired.
 *
 * Linking is optional: with no provider credentials configured the Server
 * answers `linkAccount.error.unavailable`, and this screen degrades to the
 * statement-upload path rather than becoming a dead end.
 */
export function LinkAccountScreen() {
  const navigate = useNavigate()
  const route = useRoute()
  const relinkItemId = route.query.get('relink')

  const [linking, setLinking] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  // Shown for context so the user can tell an already-linked bank from a new
  // one. Its absence is not an error — this screen works with no connections.
  const connections = useAsync((signal) => api.plaid.items(signal), [])
  const existing = connections.state.data ?? []

  const unavailable = failure?.copyKey === 'linkAccount.error.unavailable'

  const start = useCallback(async () => {
    setLinking(true)
    setFailure(null)
    try {
      const { linkToken } = relinkItemId
        ? await api.plaid.relink(relinkItemId)
        : await api.plaid.linkToken()

      const result = await openLink(linkToken)

      if (result.outcome === 'cancelled') {
        navigate('linkError', {}, { query: { reason: 'cancelled' } })
        return
      }
      if (result.outcome === 'failed') {
        navigate('linkError', {}, { query: result.code ? { code: result.code } : {} })
        return
      }

      const { connection } = await api.plaid.exchange(result.publicToken)
      navigate('linkSuccess', { id: connection.id })
    } catch (e) {
      // A failure before Link opened is this screen's error; a bank that
      // declined mid-flow is linkError's. Only the first kind lands here.
      //
      // A Link script that never loaded is the same situation as a Server with
      // no provider configured — there is no picker either way — so it reports
      // as unavailable and offers upload, rather than as a generic fault.
      setFailure(
        e instanceof LinkUnavailableError
          ? new ApiFailure(
              {
                code: 'serverError',
                copyKey: 'linkAccount.error.unavailable',
                retryable: false,
                message: e.message,
              },
              0,
            )
          : asApiFailure(e),
      )
    } finally {
      setLinking(false)
    }
  }, [navigate, relinkItemId])

  const state = unavailable ? 'unsupported' : failure ? 'error' : linking ? 'linking' : 'ready'

  return (
    <Screen id="linkAccount" state={state} back={{ screen: 'dashboard' }}>
      {state === 'unsupported' ? (
        <div className="stack">
          <ErrorState titleKey="linkAccount.error.title" error={failure ?? undefined} />
          <Button variant="primary" onClick={() => navigate('upload')}>
            {t('linkAccount.cta.upload')}
          </Button>
        </div>
      ) : (
        <div className="stack">
          {state === 'error' && failure ? <InlineError>{tServer(failure.copyKey)}</InlineError> : null}

          <div className="stack-sm">
            <Button variant="primary" onClick={start} disabled={linking}>
              {linking ? t('linkAccount.linking') : t('linkAccount.cta.link')}
            </Button>
            <span className="caption">{t('linkAccount.helper')}</span>
          </div>

          {existing.length ? (
            <List>
              {existing.map((connection) => (
                <InstitutionRow
                  key={connection.id}
                  connection={connection}
                  onSelect={() => navigate('connectedAccounts')}
                />
              ))}
            </List>
          ) : null}

          <div className="row">
            <Button variant="secondary" onClick={() => navigate('upload')}>
              {t('linkAccount.cta.upload')}
            </Button>
            {/* screens.json lists `privacyLink` here: a screen asking a bank
                for read access has to say where the data goes. */}
            <Link screen="privacy" className="btn btn-quiet">
              {t('welcome.privacy')}
            </Link>
          </div>
        </div>
      )}
    </Screen>
  )
}
